import { FC, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiDetailFields } from "@miplatform/components";
import { INPUT_TYPES, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IOrderTemplateForm } from "../ConfigOrderTemplates.types";

const FIELD_DESCRIPTIONS = {
  accountId: "The customer associated with the order.",
  documents: "Allow users to attach supporting documents or photos.",
  issue: "Capture the main problem or request reason.",
  notes: "Optional comments or clarifications.",
  orderDetails:
    "Add additional information to the order including Job Number and Purchase Order Number.",
  recipientId: "Who to reach regarding this order.",
  requestedDate:
    "Allows user to input a requested date for work to be completed.",
  requesterId: "Person or team who submitted the request.",
};

export const OrderCreationFieldsTabView: FC<
  OrderCreationFieldsTabViewProps
> = ({ form, extraTabProps: { isReadOnly } = {} }) => {
  const [selectAll, setSelectAll] = useState({
    attached: false,
    required: false,
  });

  const { control, getValues, setValue } = form;
  const { fields } = getValues();
  const { classes, theme } = useStyles();

  useEffect(() => {
    updateSelectAll();
  }, []);

  const updateSelectAll = () => {
    const isAllAttached = fields.every((f) => f.visible);
    const isAllRequired = fields.every((f) => f.required);
    setSelectAll({ attached: isAllAttached, required: isAllRequired });
  };

  const toggleAllAttached = () => {
    const isAllAttached = !selectAll.attached;
    setSelectAll((prev) => ({ ...prev, attached: isAllAttached }));
    fields.forEach((_, index) => {
      setValue(`fields.${index}.visible`, isAllAttached, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  const toggleAllRequired = () => {
    const isAllRequired = !selectAll.required;
    setSelectAll((prev) => ({
      ...prev,
      required: isAllRequired,
      attached: isAllRequired || prev.attached,
    }));
    fields.forEach((_, index) => {
      if (isAllRequired) {
        setValue(`fields.${index}.visible`, true, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      setValue(`fields.${index}.required`, isAllRequired, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  const selectAllFields = [
    {
      label: "Attached",
      name: "attached",
      fieldType: INPUT_TYPES.CHECKBOX,
      value: selectAll.attached,
      padding: 0,
      backgroundColor: "inherit",
      disabled: selectAll.required || isReadOnly,
      handleChange: toggleAllAttached,
    },
    {
      label: "Required",
      name: "required",
      fieldType: INPUT_TYPES.CHECKBOX,
      value: selectAll.required,
      padding: 0,
      backgroundColor: "inherit",
      handleChange: toggleAllRequired,
      disabled: isReadOnly,
    },
  ];

  const orderCreationFields = (index: number) => [
    {
      label: "Attached",
      name: `fields.${index}.visible`,
      fieldType: INPUT_TYPES.CHECKBOX,
      formControl: control,
      disabled: isReadOnly || fields[index].required,
      padding: 0,
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(`fields.${index}.visible`, e.target.checked, {
          shouldDirty: true,
          shouldValidate: true,
        });
        updateSelectAll();
      },
    },
    {
      label: "Required",
      name: `fields.${index}.required`,
      fieldType: INPUT_TYPES.CHECKBOX,
      formControl: control,
      disabled: isReadOnly,
      padding: 0,
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(`fields.${index}.required`, e.target.checked, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`fields.${index}.visible`, true, {
          shouldDirty: true,
          shouldValidate: true,
        });
        updateSelectAll();
      },
    },
  ];

  return (
    <ConfigAccordionHeader title="Order Creation Fields">
      {/* Select all  */}
      <Box className={classes.selectAllCard}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
          fontWeight={theme.fontWeight.semiBold}
        >
          Would you like to select all?
        </Typography>
        <MiDetailFields detailFields={selectAllFields} />
      </Box>
      {/* Individual fields */}
      {fields.map((field, index) => {
        return (
          <Box key={field.fieldName} className={classes.fieldCard}>
            <Box className={classes.fieldHeader}>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
                fontWeight={theme.fontWeight.semiBold}
              >
                {field.displayName.replace(/Issue/g, "Task")}
              </Typography>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                color={theme.palette.text.secondary}
              >
                {FIELD_DESCRIPTIONS[
                  field.fieldName as keyof typeof FIELD_DESCRIPTIONS
                ] || ""}
              </Typography>
            </Box>
            <MiDetailFields detailFields={orderCreationFields(index)} />
          </Box>
        );
      })}
    </ConfigAccordionHeader>
  );
};

const useStyles = makeStyles()((theme) => {
  const baseCardStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
    padding: theme.spacing(0, 2),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  };

  return {
    fieldCard: baseCardStyle,
    selectAllCard: {
      ...baseCardStyle,
      backgroundColor: theme.palette.primary.light,
    },
    fieldHeader: {
      display: "flex",
      gap: theme.spacing(2),
      alignItems: "center",
    },
  };
});

interface OrderCreationFieldsTabViewProps {
  form: UseFormReturn<IOrderTemplateForm>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}
